/**
 * Copyright 2023 The Ground Authors.
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  combineLatest,
  concat,
  delay,
  distinctUntilChanged,
  of,
  switchMap,
} from 'rxjs';

import {
  DialogComponent,
  DialogData,
  DialogType,
} from 'app/components/shared/dialog/dialog.component';
import { LoiPropertiesDialogComponent } from 'app/components/shared/loi-properties-dialog/loi-properties-dialog.component';
import { LocationOfInterest } from 'app/models/loi.model';
import { Survey } from 'app/models/survey.model';
import { LocationOfInterestService } from 'app/services/loi/loi.service';
import { NavigationService } from 'app/services/navigation/navigation.service';
import { SubmissionService } from 'app/services/submission/submission.service';
import { getLoiIcon } from 'app/utils/utils';

@Component({
  selector: 'ground-loi-panel',
  templateUrl: './loi-panel.component.html',
  styleUrls: ['./loi-panel.component.scss'],
  standalone: false,
})
export class LocationOfInterestPanelComponent {
  private loiService = inject(LocationOfInterestService);
  private submissionService = inject(SubmissionService);
  private navigationService = inject(NavigationService);

  activeSurvey = input<Survey>();
  selectedLoi = input<LocationOfInterest>();

  readonly isDeleting = signal(false);

  readonly isLoading = computed(() => {
    return this.submissions() === undefined;
  });

  readonly canDeleteLocationOfInterest = computed(() => {
    const survey = this.activeSurvey();
    const loi = this.selectedLoi();
    if (!survey || !loi) return false;
    return this.loiService.canDeleteLocationOfInterest(survey, loi);
  });

  readonly canDeleteSubmissions = computed(() => {
    const survey = this.activeSurvey();
    const submissions = this.submissions();
    if (!survey || !submissions) return false;
    return this.submissionService.canDeleteSubmissions(survey, submissions);
  });

  readonly name = computed(() => {
    const loi = this.selectedLoi();
    return loi ? LocationOfInterestService.getDisplayName(loi) : null;
  });

  readonly icon = computed(() => {
    const loi = this.selectedLoi();
    return loi ? getLoiIcon(loi) : '';
  });

  readonly iconColor = computed(() => {
    const survey = this.activeSurvey();
    const loi = this.selectedLoi();
    if (!survey || !loi) return '';
    return survey.getJob(loi.jobId)?.color ?? '';
  });

  constructor(private dialog: MatDialog) {}

  submissions = toSignal(
    combineLatest([
      toObservable(this.activeSurvey),
      toObservable(this.selectedLoi),
    ]).pipe(
      distinctUntilChanged(
        ([survey1, loi1], [survey2, loi2]) =>
          survey1 === survey2 &&
          loi1?.id === loi2?.id &&
          loi1?.jobId === loi2?.jobId
      ),
      switchMap(([survey, loi]) => {
        if (survey && loi) {
          return concat(
            of(undefined),
            this.submissionService.getSubmissions$(survey, loi).pipe(delay(100))
          );
        }
        return of(null).pipe(delay(100));
      })
    ),
    { initialValue: undefined }
  );

  onSelectSubmission(submissionId: string): void {
    const survey = this.activeSurvey();
    const loi = this.selectedLoi();

    if (!survey || !loi) {
      console.error('Missing survey or LOI');
      return;
    }

    this.navigationService.showSubmissionDetail(
      survey.id,
      loi.id,
      submissionId
    );
  }

  onClosePanel(): void {
    this.navigationService.clearLocationOfInterestId();
  }

  hasProperties(): boolean {
    return !!this.selectedLoi()?.properties?.size;
  }

  deleteLoi(): void {
    const survey = this.activeSurvey();
    const loi = this.selectedLoi();
    if (!survey || !loi) {
      console.error("No active survey or LOI - can't delete location");
      return;
    }
    this.dialog
      .open(DialogComponent, {
        data: {
          dialogType: DialogType.DeleteLoi,
        },
        panelClass: 'small-width-dialog',
      })
      .afterClosed()
      .subscribe(async (result: DialogData) => {
        if (!result) return;
        this.isDeleting.set(true);
        try {
          await this.loiService.deleteLocationOfInterest(survey.id, loi.id);
          this.navigationService.clearLocationOfInterestId();
        } catch (e) {
          console.error('Error deleting location', e);
        } finally {
          this.isDeleting.set(false);
        }
      });
  }

  deleteSubmissions(): void {
    const survey = this.activeSurvey();
    const loi = this.selectedLoi();
    if (!survey || !loi) {
      console.error("No active survey or LOI - can't delete submissions");
      return;
    }
    this.dialog
      .open(DialogComponent, {
        data: {
          dialogType: DialogType.DeleteSubmissions,
        },
        panelClass: 'small-width-dialog',
      })
      .afterClosed()
      .subscribe(async (result: DialogData) => {
        if (!result) return;
        this.isDeleting.set(true);
        try {
          await this.submissionService.deleteSubmissionsForLoi(survey, loi.id);
        } catch (e) {
          console.error('Error deleting submissions', e);
        } finally {
          this.isDeleting.set(false);
        }
      });
  }

  openPropertiesDialog(event: Event): void {
    event.stopPropagation();
    const loi = this.selectedLoi();
    if (!loi) return;
    this.dialog.open(LoiPropertiesDialogComponent, {
      width: '580px',
      height: '70%',
      autoFocus: false,
      data: {
        iconColor: this.iconColor(),
        iconName: this.icon(),
        loiDisplayName: this.name(),
        properties: loi.properties.toObject(),
      },
      panelClass: 'loi-properties-dialog-container',
    });
  }
}
